import { useState } from "react";
import { Button, Tabs, Tag } from "antd";
import type { ColumnsType } from "antd/es/table/interface";

import { MetadataDashboardTemplate } from "@/components/Templates/MetadataDashboardTemplate";
import PageHeader from "@/components/UI/PageHeader";
import { useScopeParams } from "@/hooks/useScopeParams";
import ClinicalReviewCycle from "./ClinicalReviewCycle";
import ClinicalReviewDrawer from "./ClinicalReviewDrawer";
import { CDR_STATUS_COLOR, CDR_STATUS_LABEL } from "./fieldLabel";

const DATASET_TYPE = "cdr_review_items";

export default function ClinicalReview() {
  const { id: trialId } = useScopeParams();
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  if (!trialId) return null;

  const extraColumns: ColumnsType<Record<string, unknown>> = [
    {
      key: "cdr_review_action",
      title: "",
      width: 110,
      fixed: "right",
      render: (_: unknown, row: Record<string, unknown>) => {
        const status = row.status as string | undefined;
        const flagCount = Number(row.flag_count ?? 0);
        return (
          <div className="flex flex-col items-start gap-1">
            {status && (
              <Tag color={CDR_STATUS_COLOR[status]}>
                {CDR_STATUS_LABEL[status] ?? status}
              </Tag>
            )}
            {flagCount > 0 && (
              <Tag color="gold">
                {flagCount} flag{flagCount === 1 ? "" : "s"}
              </Tag>
            )}
            <Button
              size="small"
              onClick={() => setSelectedItemId(row.id as number)}
            >
              Review
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex w-full min-w-0 flex-col gap-3 p-4">
      <PageHeader
        title="Clinical Data Review"
        tooltipText="Review COA/PRO responses and quantitative assessments for scientific plausibility — is the clinical data behaving the way the science says it should? Distinct from Medical Review (safety) and data management review (completeness)."
        dataTestId="clinical-review-header"
      />

      <Tabs
        defaultActiveKey="review"
        items={[
          {
            key: "review",
            label: "Review",
            children: (
              <div key={refreshToken}>
                <MetadataDashboardTemplate
                  type={DATASET_TYPE}
                  constants={{
                    wikiLink: "/wiki/guides/clinical-data-review",
                    idLink: undefined,
                  }}
                  rowKey="id"
                  extraColumns={extraColumns}
                  onRow={(row) => ({
                    onClick: () => setSelectedItemId(row.id as number),
                    style: { cursor: "pointer" },
                  })}
                />
              </div>
            ),
          },
          {
            key: "cycle",
            label: "Cycle",
            children: <ClinicalReviewCycle trialId={trialId} />,
          },
        ]}
      />

      <ClinicalReviewDrawer
        trialId={trialId}
        itemId={selectedItemId}
        onClose={() => setSelectedItemId(null)}
        onDispositioned={() => setRefreshToken((token) => token + 1)}
      />
    </div>
  );
}
